import { createContext, useRef, useState } from "react";
import { useTimelinePlayback, useVideoPlayback, useVisTimeline } from "./hooks";
import { useViewSettings } from "@/view-settings";
import { useEffectOnce, useLocalStorage } from "react-use";
import { useSyncVideo } from "./use-sync-video";
import { useBufferController } from "@/services/buffer-controller";
import { batch } from "@legendapp/state";
import { ControlEvent, emitter } from "@/services/event-emitter";
import { getInitialDateRange } from "@/shared/helpers/buffer";
import {
  LastDetectionsMapping,
  RawDetection,
  RawSnapshot,
  TrackID,
} from "@/shared/types";
import { toast } from "react-hot-toast";
import { BroadcastChannel } from "broadcast-channel";
import { TimelinePlaybackState } from "./types";
import {
  calculateSeekFromDVRWindowStart,
  consolePrint,
  getTimezone,
} from "@/shared";
import {
  calculateSeekValue,
  transformJsonToDetections,
  transformJsonToSnapshots,
} from "./helpers";
import { getDateTime } from "@/services/buffer-controller/utils";
import {
  detectionToTrackMap$,
  importedDetections$,
  resetGlobalObservables,
} from "@/virtual-store/state";
import { useAppDispatch } from "@/app/appStore";
import { setPlanogramDateRange } from "@/entities/planogram";

type PlaybackMediator = {
  play: (params?: { broadcast?: boolean }) => void;
  pause: (params?: { broadcast?: boolean }) => void;
  seek: (dateTime: Date, id: string) => void;
  seekVideo: (dateTime: Date, shouldPause?: boolean) => void;
  seeked: (dateTime: Date, id: string) => void;
  stepForward: () => void;
  stepBackward: () => void;
  seekForward: () => void;
  seekBackward: () => void;
  startLive: () => void;
  startHistory: (
    startDate?: Date,
    endDate?: Date,
    skipBuffering?: boolean
  ) => void;
  stopLive: () => void;
  startDetections: (startDate?: Date) => void;
  toggleVideo: () => void;
  importJSON: ({
    tracks,
    detections,
  }: {
    tracks: RawSnapshot[] | null;
    detections: RawDetection[] | null;
  }) => void;
  toggleDetectionsMode: (cameraId: string) => void;
  toggleStandaloneMode: () => void;
  syncRenderingWithVideo: () => void;
  selectCamera: (cameraId: string) => void;
};

export const PlaybackMediatorContext = createContext<PlaybackMediator>(
  {} as PlaybackMediator
);

enum PlayMode {
  Play = "PLAY",
  PlayLive = "PLAY_LIVE",
  PlayWithVideo = "PLAY_WITH_VIDEO",
  PlayLiveWithVideo = "PLAY_LIVE_WITH_VIDEO",
}

export function PlaybackMediatorProvider({
  children,
}: React.PropsWithChildren) {
  const [tracksBuffer, _, detectionsBuffer, reprojectionsBuffer] =
    useBufferController();
  const timelinePlayback = useTimelinePlayback();
  const timelineRef = useVisTimeline();
  const prevPlaybackState = useRef<TimelinePlaybackState | null>(null);
  const [broadcastChannel] = useState(
    () => new BroadcastChannel("playbackChannel")
  );

  const dispatch = useAppDispatch();
  const videoPlayback = useVideoPlayback();
  const viewSettings = useViewSettings();
  const syncVideo = useSyncVideo();

  useEffectOnce(() => {
    broadcastChannel.onmessage = (event) => {
      if (event.type === "play") {
        play({ broadcast: false });
      }
      if (event.type === "pause") {
        pause({ broadcast: false });
      }

      const currentTime = timelinePlayback.currentTime.peek();
      if (event.type === "seek") {
        seek(new Date(currentTime + event.delta), event.id, false);
      }
      if (event.type === "seeked") {
        seek(new Date(currentTime + event.delta), event.id, false);
      }
    };
  });

  const getPlayMode = (): PlayMode => {
    const isLive = timelinePlayback.liveMeta.isLive.peek();
    const isVideoVisible = viewSettings.showVideo.peek();

    if (isVideoVisible && isLive) {
      return PlayMode.PlayLiveWithVideo;
    }
    if (isVideoVisible && !isLive) {
      return PlayMode.PlayWithVideo;
    }
    if (!isVideoVisible && isLive) {
      return PlayMode.PlayLive;
    }
    return PlayMode.Play;
  };

  const playWithVideo = () => {
    syncVideo.on();
    const difference =
      videoPlayback.videoContext.startTime.peek()?.getTime() -
      timelinePlayback.currentTime.peek();

    if (
      videoPlayback.videoContext.isStandalone.peek() ||
      !(videoPlayback.videoContext.error.peek() || difference > 0)
    ) {
      videoPlayback.dashPlayer.play();
    } else {
      timelinePlayback.playbackState.set("playing");
    }
  };

  const playLiveWithVideo = () => {
    syncVideo.on();

    const difference =
      videoPlayback.videoContext.startTime.peek()?.getTime() -
      timelinePlayback.currentTime.peek();

    if (
      videoPlayback.videoContext.isStandalone.peek() ||
      !(videoPlayback.videoContext.error.peek() || difference > 0)
    ) {
      videoPlayback.dashPlayer.play();
    } else {
      timelinePlayback.playbackState.set("playing");
    }
  };

  const pauseWithVideo = () => {
    if (videoPlayback.videoContext.isStandalone.peek()) {
      return videoPlayback.dashPlayer.pause();
    }

    const difference =
      videoPlayback.videoContext.startTime.peek()?.getTime() -
      timelinePlayback.currentTime.peek();

    const shouldPause =
      videoPlayback.dashPlayer.isPaused() &&
      timelinePlayback.playbackState.peek() === "pending";

    if (
      shouldPause ||
      videoPlayback.videoContext.error.peek() ||
      difference > 0
    ) {
      timelinePlayback.playbackState.set("paused");
    } else {
      videoPlayback.dashPlayer.pause();
      timelinePlayback.currentTime.set(videoPlayback.timeAsUTC());
    }
  };

  const pauseLiveWithVideo = () => {
    if (videoPlayback.videoContext.isStandalone.peek()) {
      return videoPlayback.dashPlayer.pause();
    }

    const difference =
      videoPlayback.videoContext.startTime.peek()?.getTime() -
      timelinePlayback.currentTime.peek();

    const shouldPause =
      timelinePlayback.playbackState.peek() === "pending" &&
      videoPlayback.dashPlayer.isPaused();

    if (
      shouldPause ||
      videoPlayback.videoContext.error.peek() ||
      difference > 0
    ) {
      timelinePlayback.playbackState.set("paused");
    } else {
      videoPlayback.dashPlayer.pause();
      timelinePlayback.currentTime.set(videoPlayback.timeAsUTC());
    }
  };

  const play: PlaybackMediator["play"] = (params) => {
    const { broadcast = true } = params || {};
    const playMode = getPlayMode();

    switch (playMode) {
      case PlayMode.Play:
        timelinePlayback.playbackState.set("playing");
        break;
      case PlayMode.PlayLive:
        timelinePlayback.playbackState.set("playing");
        break;
      case PlayMode.PlayWithVideo:
        playWithVideo();
        break;
      case PlayMode.PlayLiveWithVideo:
        playLiveWithVideo();
        break;
    }

    if (broadcast && viewSettings.syncTabs.peek()) {
      broadcastChannel.postMessage({ type: "play" });
    }
  };

  const printDebugInfo = () => {
    const startTime = timelinePlayback.liveMeta.isLive.peek()
      ? timelinePlayback.liveMeta.start.peek()?.toISOString()
      : timelinePlayback.dateRange.start.peek()?.toISOString();

    consolePrint("Timeline start time: " + startTime);
    consolePrint(
      "Video start time: " +
        videoPlayback.videoContext.startTime.peek()?.toISOString()
    );
    consolePrint(
      "Timeline current time: " +
        new Date(timelinePlayback.currentTime.peek()).toISOString()
    );
    if (viewSettings.showVideo.peek()) {
      consolePrint("Player current offset: " + videoPlayback.dashPlayer.time());
      consolePrint("Player current UTC: " + videoPlayback.timeAsUTC());
    }
  };

  const pause: PlaybackMediator["pause"] = (params) => {
    const { broadcast = true } = params || {};
    const playMode = getPlayMode();

    printDebugInfo();

    switch (playMode) {
      case PlayMode.Play:
        timelinePlayback.playbackState.set("paused");
        break;
      case PlayMode.PlayLive:
        timelinePlayback.playbackState.set("paused");
        break;
      case PlayMode.PlayWithVideo:
        pauseWithVideo();
        break;
      case PlayMode.PlayLiveWithVideo:
        pauseLiveWithVideo();
        break;
    }

    if (broadcast && viewSettings.syncTabs.peek()) {
      broadcastChannel.postMessage({ type: "pause" });
    }
  };

  const seekVideo = (time: Date, shouldPause = false) => {
    const videoStartTime = videoPlayback.videoContext.startTime.peek();

    if (videoStartTime) {
      const seekValue = calculateSeekFromDVRWindowStart(
        time,
        videoPlayback.dashPlayer
      );
      videoPlayback.dashPlayer.seek(seekValue);

      printDebugInfo();

      if (shouldPause) {
        videoPlayback.dashPlayer.pause();
      }
    }
  };

  const preventOverSeeking = async (
    time: Date,
    id: string,
    startTime: Date,
    endTime?: Date
  ) => {
    if (endTime && time > endTime) {
      timelineRef.current?.setCustomTime(endTime, id);
      return Promise.reject();
    }

    if (time < startTime) {
      timelineRef.current?.setCustomTime(startTime, id);
      return Promise.reject();
    }

    timelineRef.current?.setCustomTime(time, id);
    return Promise.resolve();
  };

  const seek = async (time: Date, id: string, broadcast = true) => {
    const isLiveMode = timelinePlayback.mode.peek() === "live";

    if (!prevPlaybackState.current) {
      prevPlaybackState.current = timelinePlayback.playbackState.peek();
    }

    try {
      if (isLiveMode) {
        const startTime = timelinePlayback.liveMeta.start.peek();
        const endTime = timelinePlayback.liveMeta.end.peek();

        if (!(startTime && endTime)) return;

        if (timelinePlayback.playbackState.peek() !== "paused") {
          pause();
        }

        await preventOverSeeking(time, id, startTime);
      } else {
        const startTime = timelinePlayback.dateRange.start.peek();
        const endTime = timelinePlayback.dateRange.end.peek();

        if (!(startTime && endTime)) return;

        pause();
        await preventOverSeeking(time, id, startTime, endTime);
      }

      if (broadcast && viewSettings.syncTabs.peek()) {
        broadcastChannel.postMessage({
          type: "seek",
          time,
          delta: time.getTime() - timelinePlayback.currentTime.peek(),
          id,
        });
      }

      batch(() => {
        timelinePlayback.isManuallySeeking.set(true);
        timelinePlayback.currentTime.set(time.getTime());
        // timelinePlayback.state.set("ended");
      });
    } catch (error) {
      console.error("Error while seeking", error);
    }
  };

  const seeked = (time: Date, id: string, broadcast = true) => {
    const startTime = timelinePlayback.dateRange.start.peek();
    const endTime = timelinePlayback.dateRange.end.peek();

    seekVideo(time);
    jumpBufferToDate(time);

    // Continue playing if state before seeking was playing or pending
    if (
      prevPlaybackState.current === "playing" ||
      prevPlaybackState.current === "pending"
    ) {
      play();
    }

    prevPlaybackState.current = null;

    if (!startTime || !endTime) return;

    if (time.getTime() < startTime.getTime()) {
      timelineRef.current?.setCustomTime(startTime, id);
      return;
    }

    // Broadcast seeked event to another tab
    if (broadcast && viewSettings.syncTabs.peek()) {
      broadcastChannel.postMessage({
        type: "seeked",
        time,
        delta: time.getTime() - timelinePlayback.currentTime.peek(),
        id,
      });
    }
  };

  const jumpBufferToDate = (dateTime: Date) => {
    const viewMode = viewSettings.viewMode.peek();

    if (viewMode === "detections") {
      reprojectionsBuffer.seekTo(dateTime);
    } else {
      tracksBuffer.seekTo(dateTime);
    }
  };

  const [persistedConfig] = useLocalStorage<Record<string, number>>("config");

  const startBufferingLive = (liveStart: Date, liveEnd: Date) => {
    if (liveStart && liveEnd) {
      tracksBuffer.init(liveStart, liveEnd, {
        isLive: true,
        timezone: getTimezone(),
        ...(persistedConfig || {}),
      });
    }
  };

  const startBufferingHistory = (start: Date, end: Date) => {
    const [startDate, endDate] = getInitialDateRange(
      start,
      persistedConfig?.chunkSize,
      false
    );
    tracksBuffer.init(startDate, endDate, {
      isLive: false,
      dateRangeEnd: end,
      timezone: timelinePlayback.dateRange.timezone.peek(),
    });
  };

  const reset = () => {
    batch(() => {
      timelinePlayback.currentTime.set(0);
      timelinePlayback.playbackState.set("idle");
      timelinePlayback.dateRange.assign({ start: null, end: null });
      timelinePlayback.mode.set("history");
      timelinePlayback.liveMeta.assign({
        isLive: false,
        start: null,
        end: null,
      });
    });

    tracksBuffer.destroy();
    detectionsBuffer.destroy();
    // reprojectionsBuffer.destroy();
    syncVideo.off();
  };

  const startHistory = (start?: Date, end?: Date, skipBuffering?: boolean) => {
    reset();

    if (start && end) {
      timelinePlayback.dateRange.assign({ start, end });
      dispatch(setPlanogramDateRange({ start, end }));

      if (!skipBuffering) {
        startBufferingHistory(start, end);
      }
      emitter.emit(ControlEvent.HistoryStarted, {
        startDate: start,
        endDate: end,
      });
    } else {
      tracksBuffer.destroy();
    }
  };

  // "2023-11-18T13:37:46.000Z",
  // "2023-10-24T10:18:00.000Z",
  const startLive = (startDate?: Date, autoplay = true) => {
    const [liveStart, liveEnd] = getInitialDateRange(
      startDate || undefined,
      persistedConfig?.startOffset
    );

    reset();

    startBufferingLive(liveStart, liveEnd);
    dispatch(setPlanogramDateRange({ start: liveStart, end: null }));

    batch(() => {
      timelinePlayback.liveMeta.isLive.toggle();
      timelinePlayback.liveMeta.start.set(liveStart);
      timelinePlayback.liveMeta.end.set(liveEnd);
      timelinePlayback.mode.set("live");
      timelinePlayback.currentTime.set(liveStart.getTime());
    });

    emitter.emit(ControlEvent.LiveStarted, { startDate: liveStart });

    if (autoplay) {
      play();
    }
  };

  const importTrackSnapshots = (
    rawData: RawSnapshot[],
    forEachDetection?: (
      detection: LastDetectionsMapping,
      trackId: TrackID
    ) => void
  ) => {
    const startDate = new Date(getDateTime(rawData[0]));
    const endDate = new Date(getDateTime(rawData[rawData.length - 1]));
    const trackSnapshots = transformJsonToSnapshots(rawData, forEachDetection);

    startDate.setMilliseconds(0);

    resetGlobalObservables();

    try {
      timelinePlayback.currentTime.set(startDate.getTime());
      tracksBuffer.import(trackSnapshots, startDate, endDate);
      tracksBuffer.updateConfig({
        dateRangeEnd: endDate,
      });
      timelinePlayback.dateRange.assign({ start: startDate, end: endDate });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const importDetections = (rawData: RawDetection[], cameraId: string) => {
    const startDate = new Date(rawData[0].timestamp);
    const endDate = new Date(rawData[rawData.length - 1].timestamp);
    const detections = transformJsonToDetections(rawData, cameraId);

    startDate.setMilliseconds(0);

    try {
      detectionsBuffer.import(detections, startDate, endDate);
      detectionsBuffer.updateConfig({
        dateRangeEnd: endDate,
      });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const importJSON = ({
    tracks,
    detections,
  }: {
    tracks: RawSnapshot[] | null;
    detections: RawDetection[] | null;
  }) => {
    detectionToTrackMap$.set({});
    importedDetections$.set(null);

    const viewMode = viewSettings.viewMode.peek();

    if (viewMode === "snapshots") {
      if (tracks && !detections) {
        importTrackSnapshots(tracks);
      } else if (tracks && detections) {
        importedDetections$.set(detections);
        viewSettings.showDetections.set(true);
        importTrackSnapshots(tracks, (detection, trackId) => {
          detectionToTrackMap$[detection.detection_id].set(trackId);
        });
      }
    }

    if (viewMode === "detections") {
      if (tracks && !detections) {
        viewSettings.viewMode.set("snapshots");
        importTrackSnapshots(tracks);
      } else if (tracks && detections) {
        importedDetections$.set(detections);
        viewSettings.showDetections.set(true);
        importTrackSnapshots(tracks, (detection, trackId) => {
          detectionToTrackMap$[detection.detection_id].set(trackId);
        });
        importDetections(detections, videoPlayback.videoContext.camera.peek());
      }
    }
  };

  const seekForward = () => {
    const currentTime = timelinePlayback.currentTime.peek();
    const seekAmount = parseFloat(timelinePlayback.seekAmount.peek());

    const newTime = currentTime + seekAmount * 1000;
    timelinePlayback.currentTime.set(newTime);

    seekVideo(new Date(newTime));
  };

  const seekBackward = () => {
    const currentTime = timelinePlayback.currentTime.peek();
    const seekAmount = parseFloat(timelinePlayback.seekAmount.peek());

    const newTime = currentTime - seekAmount * 1000;
    timelinePlayback.currentTime.set(newTime);

    seekVideo(new Date(newTime));
  };

  const stepForward = () => {
    if (tracksBuffer.isBufferEmpty$().peek()) {
      return;
    }

    const nextDateTime = tracksBuffer.getNextSnapshot(
      timelinePlayback.currentTime.peek(),
      "forward"
    );

    if (nextDateTime) {
      timelinePlayback.currentTime.set(nextDateTime.getTime());
    }
  };

  const stepBackward = () => {
    if (tracksBuffer.isBufferEmpty$().peek()) {
      return;
    }

    const nextDateTime = tracksBuffer.getNextSnapshot(
      timelinePlayback.currentTime.peek(),
      "backward"
    );

    if (nextDateTime) {
      timelinePlayback.currentTime.set(nextDateTime.getTime());
    }
  };

  const stopLive = () => {
    batch(() => {
      timelinePlayback.liveMeta.isLive.toggle();
      tracksBuffer.stopPolling();
      tracksBuffer.stopRetry();
    });
    // pause();
  };

  const restartLive = (startDate: Date) => {
    stopLive();
    startLive(startDate, false);
  };

  const syncRenderingWithVideo = () => {
    timelinePlayback.playbackState.set("paused");
    syncVideo.off();

    const currentTime = timelinePlayback.currentTime.peek();
    const currentDate = new Date(currentTime);

    if (timelinePlayback.liveMeta.isLive.peek() && currentTime) {
      restartLive(currentDate);
      emitter.emit(ControlEvent.VideoEnabled, {
        startDate: currentDate,
        endDate: null,
        isLive: true,
      });
    } else {
      const startDate = timelinePlayback.dateRange.start.peek();
      const endDate = timelinePlayback.dateRange.end.peek();

      if (startDate && endDate) {
        emitter.emit(ControlEvent.VideoEnabled, {
          startDate,
          endDate,
          seekValue: calculateSeekValue(
            currentDate,
            timelinePlayback.dateRange.start.peek()
          ),
          isLive: false,
        });
      }
    }
  };

  const toggleVideo = () => {
    const isVideoVisible = viewSettings.showVideo.peek();

    viewSettings.showVideo.toggle();

    // Emit event after video element mounted
    setTimeout(() => {
      if (isVideoVisible) {
        syncVideo.off();
        emitter.emit(ControlEvent.VideoDisabled);
      } else {
        syncRenderingWithVideo();
      }
    });
  };

  const toggleStandaloneMode = () => {
    const isStandalone = videoPlayback.videoContext.isStandalone.peek();

    videoPlayback.videoContext.isStandalone.toggle();
    if (!isStandalone) {
      play();
    }
  };

  const selectCamera = (cameraId: string) => {
    if (videoPlayback.videoContext.camera.peek() !== cameraId) {
      videoPlayback.videoContext.camera.set(cameraId);
      localStorage.setItem("selectedCamera", String(cameraId));
      toast("Video source changed to camera №" + cameraId);

      const isVideoVisible = viewSettings.showVideo.peek();

      if (isVideoVisible) {
        syncRenderingWithVideo();
      }
    }
  };

  const startDetections = (customStartDate?: Date, cameraId?: string) => {
    const importedDetections = importedDetections$.peek();

    if (importedDetections && cameraId) {
      importDetections(importedDetections, cameraId);
      return;
    }

    const start = customStartDate || timelinePlayback.dateRange.start.peek();
    const end = timelinePlayback.dateRange.end.peek();

    if (!start) return;

    const currentTime = timelinePlayback.currentTime.peek() || customStartDate;

    const [startDate, endDate] = getInitialDateRange(
      new Date(currentTime!),
      persistedConfig?.startOffset,
      false
    );

    detectionsBuffer.init(startDate, endDate, {
      isLive: false,
      dateRangeEnd: end,
      timezone: timelinePlayback.dateRange.timezone.peek(),
      cameraIds: videoPlayback.videoContext.camera.peek(),
    });
    reprojectionsBuffer.init(startDate, endDate, {
      isLive: false,
      dateRangeEnd: end,
      timezone: timelinePlayback.dateRange.timezone.peek(),
      cameraIds: videoPlayback.videoContext.camera.peek(),
    });
  };

  const stopBufferingDetections = () => {
    detectionsBuffer.destroy();
    reprojectionsBuffer.destroy();

    const startDate = timelinePlayback.dateRange.start.peek();
    const endDate = timelinePlayback.dateRange.end.peek();

    if (startDate && endDate) {
      startBufferingHistory(startDate, endDate);
    }
  };

  const toggleDetectionsMode = (cameraId: string) => {
    const viewMode = viewSettings.viewMode.peek();

    selectCamera(cameraId);

    if (viewMode === "detections") {
      stopBufferingDetections();
      viewSettings.viewMode.set("snapshots");
    } else {
      startDetections(undefined, cameraId);
      viewSettings.viewMode.set("detections");
    }
  };

  useEffectOnce(() => {
    // TODO: Remove globals on production realease
    window.dashPlayer = videoPlayback.dashPlayer;
    window.getTime = () => timelinePlayback.currentTime.peek();
    window.getBufferSnapshot = (
      datetime = timelinePlayback.currentTime.peek(),
      trackId = "1696326698053888"
    ) => tracksBuffer.getSnapshotToDraw(trackId, datetime);
    window.getDetectionSnapshot = (
      datetime = timelinePlayback.currentTime.peek(),
      trackId = "1696326698053888"
    ) => detectionsBuffer.getSnapshotToDraw(trackId, datetime);

    return syncVideo.off;
  });

  const playbackMediator: PlaybackMediator = {
    play,
    pause,
    seek,
    seekVideo,
    seeked,
    stepForward,
    stepBackward,
    seekForward,
    seekBackward,
    startLive,
    startHistory,
    stopLive,
    startDetections,
    toggleVideo,
    importJSON,
    toggleDetectionsMode,
    toggleStandaloneMode,
    syncRenderingWithVideo,
    selectCamera,
  };

  return (
    <PlaybackMediatorContext.Provider value={playbackMediator}>
      {children}
    </PlaybackMediatorContext.Provider>
  );
}
